import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SectionStatusChip } from './components/SectionStatusChip';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { AtencionFiltroFechas, rangoDe } from './components/AtencionFiltroFechas';
import { AtencionTarjetas } from './components/AtencionTarjetas';
import { AtencionGrupos } from './components/AtencionGrupos';
import { AtencionBot } from './components/AtencionBot';
import { AtencionMotivos } from './components/AtencionMotivos';
import { AtencionTiposEmail } from './components/AtencionTiposEmail';
import { AtencionSemanal } from './components/AtencionSemanal';
import { AtencionAbiertosViejos } from './components/AtencionAbiertosViejos';
import { AtencionAccionesAdmin } from './components/AtencionAccionesAdmin';
import { AtencionInformeSemanal } from './components/AtencionInformeSemanal';
import { AtencionRegistroInformes } from './components/AtencionRegistroInformes';
import { AtencionColumnasProvider } from './components/AtencionColumnasContext';
import { hoyArt } from './components/atencionUtils';
import { fmtNum } from './components/kpiUtils';
import { LifeBuoy, Loader2, Upload } from 'lucide-react';

const Tablero = ({ data }) => (
  <>
    <AtencionTarjetas r={data.resumen} />
    <AtencionGrupos grupos={data.grupos} />
    <AtencionBot bot={data.bot} />
    <AtencionMotivos motivos={data.motivos} />
    <AtencionTiposEmail key={`${data.rango?.desde}-${data.rango?.hasta}`} tipos={data.tipos_email} />
    <AtencionSemanal semanal={data.semanal} />
    <AtencionAbiertosViejos lista={data.abiertos_viejos} />
  </>
);

const KpisAtencion = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [filtro, setFiltro] = useState(() => rangoDe('mes', hoyArt().slice(0, 7)));
  const [tab, setTab] = useState('tablero');
  const [refrescando, setRefrescando] = useState(false);
  const [registroKey, setRegistroKey] = useState(0);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    setRefrescando(true);
    try {
      const r = await api.get('/kpis/atencion', { params: { desde: filtro.desde, hasta: filtro.hasta } });
      setData(r.data);
    } catch (e) { toast.error('Error al cargar Atención al Cliente'); }
    finally { setLoading(false); setRefrescando(false); }
  }, [filtro.desde, filtro.hasta]);
  useEffect(() => { load(); }, [load]);

  const subir = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/atencion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success(`${fmtNum(r.data.total)} tickets: ${fmtNum(r.data.nuevos)} nuevos y ${fmtNum(r.data.actualizados)} actualizados`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-atencion-loading">Cargando tickets…</div>;

  return (
    <AtencionColumnasProvider inicial={data?.columnas_config} puede={!!data?.puede_config_columnas}>
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-atencion">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-sky-500/10"><LifeBuoy className="h-6 w-6 text-sky-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Atención al Cliente</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-atencion" /></div>
            <p className="text-sm text-slate-500">Tickets por grupo, bot de WhatsApp, motivos, tipos de email, semanas y abiertos</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="atencion" testId="atencion-help-btn" />
          <SectionExportButton title="Atención al Cliente" targetRef={rootRef} testId="atencion-export-btn" />
          {data?.puede_informe && (
            <AtencionInformeSemanal semanas={data.semanas_informe} catalogo={data.columnas_export} onDescargado={() => setRegistroKey((k) => k + 1)} />
          )}
          {data?.puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-sky-600 hover:bg-sky-700 active:scale-95 transition-transform" data-testid="atencion-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir tickets (CSV)'}
              </Button>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={subir} data-testid="atencion-file-input" />
            </>
          )}
        </div>
      </div>

      {data?.es_admin && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3" data-testid="atencion-admin-acciones">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Administrador</span>
          <AtencionAccionesAdmin onDone={load} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {data?.puede_ver_registro && (
          <TabsList className="mb-4" data-testid="atencion-tabs">
            <TabsTrigger value="tablero" data-testid="atencion-tab-tablero">Tablero</TabsTrigger>
            <TabsTrigger value="registro" data-testid="atencion-tab-registro">Registro de informes</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="tablero" className="mt-0">
          {!data?.cargado ? (
            <Card className="p-10 text-center border-dashed border-2 border-slate-200" data-testid="atencion-empty">
              <LifeBuoy className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Todavía no hay tickets cargados.</p>
              <p className="text-sm text-slate-400 mt-1">{data?.puede_cargar ? 'Subí el CSV de tickets o traelos de Freshdesk.' : 'Pedile a un administrador que cargue el reporte.'}</p>
            </Card>
          ) : (
            <>
              <AtencionFiltroFechas filtro={filtro} onChange={setFiltro} />
              <div className={`relative transition-opacity duration-200 ${refrescando ? 'pointer-events-none opacity-50' : ''}`} aria-busy={refrescando}>
                {refrescando && (
                  <div className="absolute inset-x-0 top-2 z-10 flex justify-center" data-testid="atencion-refrescando">
                    <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-sky-700 shadow"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Actualizando…</span>
                  </div>
                )}
                <Tablero data={data} />
              </div>
              <p className="text-xs text-slate-400 mt-4" data-testid="atencion-pie">
                Fuente: {data.archivo || 'Export de tickets'} · última actualización {data.fecha_carga ? new Date(data.fecha_carga).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '—'}
                {' · '}{fmtNum(data.excluidos_sistema)} tickets de sistema excluidos en el período · {fmtNum(data.total_guardados)} tickets guardados en total
              </p>
            </>
          )}
        </TabsContent>
        {data?.puede_ver_registro && (
          <TabsContent value="registro" className="mt-0"><AtencionRegistroInformes key={registroKey} /></TabsContent>
        )}
      </Tabs>
    </div>
    </AtencionColumnasProvider>
  );
};

export default KpisAtencion;
